import { computed } from "vue";
import { useRoute } from "vue-router/composables";
import { isEmpty, isNil } from "lodash-es";

import useLegacyStore from "@Composables/useLegacyStore";

import BILLABLE_TYPE from "@Constants/billable-type";
import SUBSCRIPTION_TYPE from "@Constants/subscription-type";

export default function useBillable() {
  const route = useRoute();
  const { getters } = useLegacyStore();

  const currentUser = computed(() => getters.user);
  const isUserLoggedIn = computed(() => getters.isUserLoggedIn);
  const currentOrganization = computed(() => getters.organization);
  const currentOrganizationTeam = computed(() => getters.organizationTeam);

  const billableType = computed(() => {
    const fullRoutePath = route?.fullPath;
    const inOrganization = fullRoutePath.includes("/organizations/");

    if (inOrganization) return BILLABLE_TYPE.ORGANIZATION;
    return BILLABLE_TYPE.USER;
  });
  const currentBillable = computed(() => {
    switch (billableType.value) {
      case BILLABLE_TYPE.USER: return getBillableUser();
      case BILLABLE_TYPE.TEAM: return getBillableTeam();
      case BILLABLE_TYPE.ORGANIZATION: return getBillableOrganization();
      default: throw new Error("Unsupported billable type");
    }
  });

  const isBillingUser = computed(() => {
    return billableType.value && billableType.value === BILLABLE_TYPE.USER;
  });
  const isBillingTeam = computed(() => {
    return billableType.value && billableType.value === BILLABLE_TYPE.TEAM;
  });
  const isBillingOrganization = computed(() => {
    return (
      billableType.value && billableType.value === BILLABLE_TYPE.ORGANIZATION
    );
  });

  function getBillableUser() {
    /**
     * Gets the payment method information
     *
     * @returns The payment method information,
     */
    function getUserPaymentMethod() {
      const billingCollection = currentUser.value?.billing_customers;

      if (isNil(billingCollection) || isEmpty(billingCollection)) {
        return {};
      }

      const paymentMethod = billingCollection[0];

      return {
        pm_type: paymentMethod.pm_type,
        pm_last_four: paymentMethod.pm_last_four,
        pm_expiration: paymentMethod.pm_expiration,
        pm_country: paymentMethod.pm_country,
      };
    }
    /**
     * Gets the current subscription.
     *
     * @returns The current subscription information.
     *
     * @remarks FIXME: The [0] is a temporary solution only to identify current subscription
     */
    function getUserSubscriptions() {
      if (!isUserLoggedIn.value) {
        return {};
      }

      let currentSubscriptions = currentUser.value?.subscriptions?.current;
      currentSubscriptions = currentSubscriptions?.filter((d) => {
        return d.type === SUBSCRIPTION_TYPE.DEFAULT;
      });

      return {
        currentSubscription: currentSubscriptions?.[0] ?? null,
      };
    }

    return {
      ...currentUser.value,
      ...getUserPaymentMethod(),
      ...getUserSubscriptions(),
    };
  }
  function getBillableTeam() {
    return {
      ...currentOrganizationTeam.value,
      currentSubscription: {},
    };
  }
  function getBillableOrganization() {
    return {
      ...currentOrganization.value,
      currentSubscription: {},
    };
  }

  return {
    billableType,
    currentBillable,
    isBillingUser,
    isBillingTeam,
    isBillingOrganization,
  };
}
